import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Box, Button, Divider, Typography, Skeleton } from '@mui/material';
import clsx from 'clsx';
import { BottomDialog } from 'lib/BottomDialog';
import { QuantitySelector } from 'lib/QuantitySelector';
import { ISuggestions } from '../model/Menu';

const PREFIX = 'MenuSuggestionsBogof';

const classes = {
    title: `${PREFIX}title`,
    suggestionBox: `${PREFIX}suggestionBox`,
    descriptionBox: `${PREFIX}descriptionBox`,
    suggestionDescription: `${PREFIX}suggestionDescription`,
    imageWrapper: `${PREFIX}imageWrapper`,
    image: `${PREFIX}image`,
    imageHidden: `${PREFIX}imageHidden`,
    quantityBox: `${PREFIX}quantityBox`,
    divider: `${PREFIX}divider`,
    skeleton: `${PREFIX}skeleton`
};

const StyledBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.5, 2),
    [`& .${classes.title}`]: {
        marginBottom: theme.spacing(0.5),
        fontSize: theme.spacing(2.5),
        fontFamily: `TenantFont, ${theme.typography.fontFamily}`,
        color: theme.palette.titleTextColour
    },

    [`& .${classes.suggestionBox}`]: {
        marginBottom: theme.spacing(1.5),
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius
    },

    [`& .${classes.descriptionBox}`]: {
        padding: theme.spacing(1.5, 2)
    },

    [`& .${classes.suggestionDescription}`]: {
        minHeight: '1.5rem'
    },

    [`& .${classes.imageWrapper}`]: {
        width: '100%',
        display: 'flex'
    },

    [`& .${classes.image}`]: {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    },

    [`& .${classes.imageHidden}`]: {
        visibility: 'hidden',
        width: 0,
        height: 0,
        padding: 0,
        margin: 0
    },

    [`& .${classes.quantityBox}`]: {
        marginBottom: theme.spacing(0.5)
    },

    [`& .${classes.divider}`]: {
        marginBottom: theme.spacing(1.5)
    },

    [`& .${classes.skeleton}`]: {
        height: '120px',
        width: '100%'
    }
}));

interface SuggestionsBogofProps {
    open: boolean;
    onClose: () => void;
    changeQuantity: (newQuantity: number) => void;
    suggestion: ISuggestions;
    quantity: number;
    currency: string;
}

export const MenuSuggestionsBogof: React.FC<React.PropsWithChildren<SuggestionsBogofProps>> = ({
    open,
    onClose,
    changeQuantity,
    suggestion,
    quantity
}) => {
    const { description = '', title, imageUrl } = suggestion;

    const { t } = useTranslation();
    const [newQuantity, setNewQuantity] = React.useState(quantity);
    const [imageLoaded, setImageLoaded] = React.useState(false);

    const handleClickNewQuantity = React.useCallback(() => {
        changeQuantity(newQuantity);
    }, [changeQuantity, newQuantity]);

    const handleImageLoaded = React.useCallback(() => {
        setImageLoaded(true);
    }, []);

    const suggestionDescription = React.useMemo(
        () => (description ? description.split('\n') : []),
        [description]
    );

    const renderProduct = React.useCallback(
        (item: any, index: any) => (
            <Typography
                key={`suggestion-description-${index}`}
                className={classes.suggestionDescription}
                align="center"
            >
                {item}
            </Typography>
        ),
        []
    );
    return (
        <BottomDialog open={open} onClose={onClose}>
            <StyledBox>
                <Typography className={classes.title} align="center">
                    {title ? title : t('RELATED_PRODUCT_ADD_TITLE')}
                </Typography>
                <Divider className={classes.divider} />
                <Box className={classes.suggestionBox}>
                    {imageUrl && (
                        <>
                            <Box
                                className={clsx(classes.imageWrapper, {
                                    [classes.imageHidden]: !imageLoaded
                                })}
                            >
                                <img className={classes.image} src={imageUrl} onLoad={handleImageLoaded} />
                            </Box>
                            {!imageLoaded && <Skeleton className={classes.skeleton} variant="rectangular" />}
                        </>
                    )}
                    {suggestionDescription.length > 0 && (
                        <Box className={classes.descriptionBox}>
                            {suggestionDescription.map(renderProduct)}
                        </Box>
                    )}
                </Box>
                <Box
                    className={classes.quantityBox}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                >
                    <QuantitySelector min={1} max={999} quantity={newQuantity} onChange={setNewQuantity} />
                </Box>
                <Button color="primary" variant="contained" onClick={handleClickNewQuantity} fullWidth>
                    {t('RELATED_PRODUCT_BOGOF_ACCEPT')}
                </Button>
            </StyledBox>
        </BottomDialog>
    );
};
